import { ReactElement, useMemo, useRef, useState } from 'react'
import { get2DLineOrthoDirection, infiniteLineIntersection } from '@editorUtils'
import { DrawControllerWrapper, TargetPlane, toVector2 } from '@scene'
import { isNull, round } from 'lodash-es'
import { Plane as PlaneThree } from 'three'
import {
  ImmutableVector3,
  TransientDrawState,
  InteractiveLine,
  ScalingIndicator,
  InteractiveLineRef,
  InteractiveLineHandles,
} from '@modugen/ui-toolkit'
import { useEditElementStore } from '@editorStores'
import { useClickActionEnabled, useHideElements } from '@editorHooks'
import { nthLooped } from '@utils'
import { roofDrawingDecimals } from 'src/components/pages/Editor/config'
import { SlabFormSchema } from '../RoofForm/schema'

interface Props {
  points: SlabFormSchema['points']
  updatePoints: (points: SlabFormSchema['points']) => void
  element: string
}

const SlabDrawing = ({ points, updatePoints, element }: Props): ReactElement => {
  const activeRoofPointIndex = useEditElementStore(state => state.activeRoofPointIndex)

  const edgeLineRefs = useRef<Array<InteractiveLineRef>>([])

  const [activeEdgeIndex, setActiveEdgeIndex] = useState<null | number>(null)

  // MEMOS

  const slabPoints = useMemo(() => {
    return points?.map(p => new ImmutableVector3(p.x, p.y, p.z))
  }, [points])

  const slabZ = slabPoints?.[0]?.z ?? 0

  const edges = useMemo(
    () =>
      slabPoints?.map((p, i) => {
        const nextP = nthLooped(slabPoints, i + 1)

        return [p, nextP]
      }),
    [slabPoints],
  )

  // EFFECTS

  useHideElements(element)

  // EVENTS
  const moveRoofEdge = (state: TransientDrawState) => {
    if (isNull(activeEdgeIndex) || !state.drawPoint || !slabPoints) return

    // What we basically do here is use the current line to create a plane that
    // is vertical and extend the previous and next line to find any
    // intersection point on the plane to update the corner points of the roof

    const edge = edgeLineRefs.current[activeEdgeIndex]

    if (!edge.transientLine.current) return

    if (edges) {
      const [edgeStart, edgeEnd] = edges[activeEdgeIndex]

      const edgeOrthogonalDirection2 = get2DLineOrthoDirection(
        toVector2(edgeStart),
        toVector2(edgeEnd),
      )

      const edgeOrthogonalDirection = new ImmutableVector3(
        edgeOrthogonalDirection2.x,
        edgeOrthogonalDirection2.y,
      )

      const cuttingPlane = new PlaneThree().setFromNormalAndCoplanarPoint(
        edgeOrthogonalDirection.v,
        state.drawPoint.v,
      )

      const [previousEdgeStart, previousEdgeEnd] = nthLooped(edges, activeEdgeIndex - 1)
      const [nextEdgeStart, nextEdgeEnd] = nthLooped(edges, activeEdgeIndex + 1)

      const intersectionPrevious = infiniteLineIntersection(
        cuttingPlane,
        previousEdgeStart,
        previousEdgeEnd,
      )

      const intersectionNext = infiniteLineIntersection(cuttingPlane, nextEdgeStart, nextEdgeEnd)

      if (intersectionPrevious && intersectionNext) {
        const intersectionPreviousOnSlab = new ImmutableVector3(
          round(intersectionPrevious.x, roofDrawingDecimals),
          round(intersectionPrevious.y, roofDrawingDecimals),
          slabZ,
        )

        const intersectionNextOnSlab = new ImmutableVector3(
          round(intersectionNext.x, roofDrawingDecimals),
          round(intersectionNext.y, roofDrawingDecimals),
          slabZ,
        )

        const previousLineRef = nthLooped(edgeLineRefs.current, activeEdgeIndex - 1)
        previousLineRef.updateHandle(InteractiveLineHandles.End, intersectionPreviousOnSlab)

        const nextLineRef = nthLooped(edgeLineRefs.current, activeEdgeIndex + 1)
        nextLineRef.updateHandle(InteractiveLineHandles.Start, intersectionNextOnSlab)

        edge.updateLine([intersectionPreviousOnSlab, intersectionNextOnSlab])
      }
    }
  }

  const drawEnd = () => {
    if (!element || !edges || isNull(activeEdgeIndex)) return

    const roofPoints = edgeLineRefs.current.map(line => {
      if (line?.transientLine.current) {
        return line.transientLine.current[0]
      }
      return edges[activeEdgeIndex][0]
    })

    const newRoofPoints = roofPoints?.map(p => ({
      x: p.x,
      y: p.y,
      z: slabZ,
    }))

    updatePoints(newRoofPoints)

    setActiveEdgeIndex(null)
  }

  const drawControllerEnabled = useClickActionEnabled()

  if (!slabPoints) return <></>

  return (
    <>
      {/* DrawController used for moving the edges of an existing roof shape */}
      <DrawControllerWrapper
        enabled={!isNull(activeEdgeIndex) && drawControllerEnabled}
        onMouseMove={moveRoofEdge}
        onDrawEnd={drawEnd}
        enableIndicator={false}
        isValidDrawTarget={object => object.name === 'draw-plane'}
      />

      {/* Showing the corner the user is currently editing in the scene (if textfield is active in the form) */}
      <ScalingIndicator
        position={slabPoints[activeRoofPointIndex || 0].v}
        color="blue"
        visible={!isNull(activeRoofPointIndex)}
      />

      {edges?.map(([start, end], i) => (
        <InteractiveLine
          key={`${element}+${i}-${start.x}-${start.y}-${start.z}-${end.x}-${end.y}-${end.z}-`}
          ref={element => {
            edgeLineRefs.current[i] = element as InteractiveLineRef
          }}
          line={[start, end]}
          color={i === activeEdgeIndex ? 'blue' : '#707070'}
          disableDepthTest
          isSelected={activeEdgeIndex === i}
          onClick={() => setActiveEdgeIndex(i)}
          showHandles={false}
          nonSelectable={!isNull(activeEdgeIndex)}
          clickDisabled={!isNull(activeEdgeIndex)}
        />
      ))}

      {slabPoints && <TargetPlane points={slabPoints} />}
    </>
  )
}

export default SlabDrawing
